import React from 'react';
import { Box, Card, CardContent, Typography, useTheme } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { IndustrySectorDistribution as IndustrySectorDistributionType } from '../../../types/utility.types';

interface IndustrySectorDistributionProps {
  data: IndustrySectorDistributionType[];
  isLoading: boolean;
}

const IndustrySectorDistribution: React.FC<IndustrySectorDistributionProps> = ({ data, isLoading }) => {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Industry Sector Distribution
          </Typography>
          <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography>Loading...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Filter out any items with count 0 and sort by count (descending)
  const chartData = data
    .filter(item => item.count > 0)
    .sort((a, b) => b.count - a.count)
    .map(item => ({
      sector: item.sector || 'Unknown',
      count: item.count
    }))
    .slice(0, 10); // Show top 10 sectors

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Industry Sector Distribution
        </Typography>
        {chartData.length > 0 ? (
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
                layout="vertical"
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis 
                  type="category" 
                  dataKey="sector" 
                  width={120}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip formatter={(value) => [`${value} clients`, 'Count']} />
                <Legend />
                <Bar dataKey="count" name="Clients" fill={theme.palette.primary.main} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography>No data available</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default IndustrySectorDistribution;
